import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ensureFreshToken } from '../auth/tokenRefresh';

const AI_ENDPOINT = (import.meta as any).env?.VITE_AI_ENDPOINT ?? 'http://localhost:3020';

/**
 * Owns the AI Chat session for one app: starts a session, streams
 * messages via SSE-over-fetch, dispatches events into local React
 * state, and debounces canvas-data invalidations so the iframe doesn't
 * rebuild for every individual tool call.
 */

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  /** Tool calls observed during this turn, in order. */
  tools?: ToolCall[];
}

export interface ToolCall {
  name: string;
  args: unknown;
  status: 'pending' | 'executing' | 'done' | 'failed';
  result?: unknown;
}

export interface PendingConfirm {
  tool: string;
  args: unknown;
  reason: string;
}

interface SessionInfo {
  sessionId: string;
  appId: string;
  /** The model the server locked in for this session (sticky-per-session). */
  model: string;
  /** Whether the picked model overrode the server default. */
  modelSource: 'default' | 'override';
}

type SseEvent =
  | { type: 'text'; payload: { delta: string } }
  | { type: 'tool'; payload: { name: string; args: unknown; status: 'pending' | 'executing' | 'done' | 'failed'; result?: unknown } }
  | { type: 'change'; payload: { viewIds: string[]; summary: string } }
  | { type: 'needs_confirm'; payload: { tool: string; args: unknown; reason: string } }
  | { type: 'done'; payload: { usage: { input_tokens: number; output_tokens: number } } }
  | { type: 'error'; payload: { message: string } };

const INVALIDATE_DEBOUNCE_MS = 500;

export function useAISession(appId: string | undefined) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Picked model for the chat session. Forwarded to /start the FIRST
   * time we send a message; locked thereafter (sticky-per-session on
   * the server). `undefined` = use server default.
   */
  const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined);

  const aborterRef = useRef<AbortController | null>(null);
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushInvalidations = useCallback(() => {
    if (invalidateTimerRef.current) {
      clearTimeout(invalidateTimerRef.current);
      invalidateTimerRef.current = null;
    }
    if (!appId) return;
    queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
    queryClient.invalidateQueries({ queryKey: ['nav-views', appId] });
  }, [appId, queryClient]);

  const scheduleInvalidate = useCallback(() => {
    if (invalidateTimerRef.current) clearTimeout(invalidateTimerRef.current);
    invalidateTimerRef.current = setTimeout(flushInvalidations, INVALIDATE_DEBOUNCE_MS);
  }, [flushInvalidations]);

  /** Start a session lazily on the first message. */
  const ensureSession = useCallback(async (): Promise<SessionInfo> => {
    if (session) return session;
    if (!appId) throw new Error('No appId for AI session');
    const token = await ensureFreshToken();
    const res = await fetch(`${AI_ENDPOINT}/ai/edit/start`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      // Forward the selected model only when set — the server
      // validates against AI_ALLOWED_CHAT_MODELS and rejects unknown
      // slugs. Once /start completes, the model is locked for this
      // session (mid-conversation swaps invalidate prompt cache and
      // produce jarring tonal shifts).
      body: JSON.stringify({ appId, ...(selectedModel ? { model: selectedModel } : {}) }),
    });
    if (!res.ok) throw new Error(`Failed to start session: ${res.status}`);
    const json = await res.json();
    const info: SessionInfo = {
      sessionId: json.sessionId,
      appId,
      model: json.model,
      modelSource: json.modelSource ?? 'default',
    };
    setSession(info);
    return info;
  }, [session, appId, selectedModel]);

  const send = useCallback(
    async (text: string, selectedViewId?: string) => {
      if (isStreaming) return;
      setError(null);
      const userMsgId = crypto.randomUUID();
      const assistantMsgId = crypto.randomUUID();
      setMessages((m) => [
        ...m,
        { id: userMsgId, role: 'user', text },
        { id: assistantMsgId, role: 'assistant', text: '', tools: [] },
      ]);
      setIsStreaming(true);

      try {
        const info = await ensureSession();
        const token = await ensureFreshToken();
        const aborter = new AbortController();
        aborterRef.current = aborter;
        const res = await fetch(
          `${AI_ENDPOINT}/ai/edit/${info.sessionId}/message`,
          {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
              accept: 'text/event-stream',
              ...(token ? { authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ text, selectedViewId }),
            signal: aborter.signal,
          },
        );
        if (!res.ok || !res.body) throw new Error(`SSE failed: ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let assistantText = '';
        let assistantTools: ToolCall[] = [];

        const updateAssistant = () => {
          setMessages((m) =>
            m.map((msg) =>
              msg.id === assistantMsgId
                ? { ...msg, text: assistantText, tools: [...assistantTools] }
                : msg,
            ),
          );
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Parse SSE: event: <name>\ndata: <json>\n\n
          let idx;
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const block = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const evt = parseSseBlock(block);
            if (!evt) continue;
            switch (evt.type) {
              case 'text':
                assistantText += evt.payload.delta;
                updateAssistant();
                break;
              case 'tool': {
                const existing = assistantTools.findIndex(
                  (t) => t.name === evt.payload.name && t.status !== 'done' && t.status !== 'failed',
                );
                if (existing >= 0) {
                  assistantTools[existing] = { ...assistantTools[existing], ...evt.payload };
                } else {
                  assistantTools.push({
                    name: evt.payload.name,
                    args: evt.payload.args,
                    status: evt.payload.status,
                    result: evt.payload.result,
                  });
                }
                updateAssistant();
                break;
              }
              case 'change':
                scheduleInvalidate();
                break;
              case 'needs_confirm':
                setPendingConfirm(evt.payload);
                break;
              case 'done':
                flushInvalidations();
                break;
              case 'error':
                setError(evt.payload.message);
                break;
            }
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg !== 'AbortError') setError(msg);
      } finally {
        setIsStreaming(false);
        aborterRef.current = null;
        flushInvalidations();
      }
    },
    [ensureSession, isStreaming, flushInvalidations, scheduleInvalidate],
  );

  const stop = useCallback(() => {
    if (aborterRef.current) {
      aborterRef.current.abort();
      aborterRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const respondToConfirm = useCallback(
    async (proceed: boolean) => {
      if (!session || !pendingConfirm) return;
      setPendingConfirm(null);
      const token = await ensureFreshToken();
      await fetch(`${AI_ENDPOINT}/ai/edit/${session.sessionId}/confirm`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ proceed }),
      });
    },
    [session, pendingConfirm],
  );

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (aborterRef.current) aborterRef.current.abort();
      if (invalidateTimerRef.current) clearTimeout(invalidateTimerRef.current);
    };
  }, []);

  return {
    messages,
    isStreaming,
    pendingConfirm,
    error,
    send,
    stop,
    respondToConfirm,
    /**
     * Picker state. The chat panel renders <ModelPicker> against
     * `selectedModel` + `setSelectedModel`. After /start runs (lazy,
     * on first send), `session.model` reflects what the server
     * actually locked in — the panel uses that to display the active
     * slug and disable further changes.
     */
    selectedModel,
    setSelectedModel,
    /** The model in use for this session, once /start has happened. */
    sessionModel: session?.model,
    sessionModelSource: session?.modelSource,
    /** True after /start has completed — picker should lock here. */
    sessionStarted: session !== null,
  };
}

function parseSseBlock(block: string): SseEvent | null {
  let eventType = '';
  let dataStr = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
  }
  if (!eventType || !dataStr) return null;
  try {
    return { type: eventType as SseEvent['type'], payload: JSON.parse(dataStr) } as SseEvent;
  } catch {
    return null;
  }
}
